import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { api, getToken, setToken, errMsg } from '../lib/api';
import { useCatalog } from './CatalogContext';

const AppContext = createContext(null);

const EMPTY = { myList: [], following: [], progress: {}, history: [], notifications: [], profiles: [], activeProfileId: null, interests: [], country: 'NG', subscription: null };

export function AppProvider({ children }) {
  const { byId } = useCatalog();
  const [user, setUser] = useState(null);
  const [state, setState] = useState(EMPTY);
  const [authLoading, setAuthLoading] = useState(!!getToken());
  const [homeCfg, setHomeCfgState] = useState({ heroSlides: [], rows: [], panels: [] });
  const [guestCountry, setGuestCountry] = useState(() => localStorage.getItem('clax:country') || 'NG');

  // detail modal
  const [detailId, setDetailId] = useState(null);
  // audio mini-player
  const [audio, setAudio] = useState({ podcastId: null, episode: null, playing: false, currentTime: 0, duration: 0, volume: 0.8 });
  const audioRef = useRef(null);

  const loadState = useCallback(async () => {
    if (!getToken()) { setUser(null); setState(EMPTY); setAuthLoading(false); return; }
    try {
      const r = await api.get('/me/state');
      setUser(r.data.user);
      setState({ ...EMPTY, ...r.data });
    } catch (e) {
      if (e?.response?.status === 401) { setToken(null); setUser(null); setState(EMPTY); }
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const loadHome = useCallback(async () => {
    try { const r = await api.get('/home'); setHomeCfgState(r.data); } catch { /* keep defaults */ }
  }, []);

  useEffect(() => { loadState(); loadHome(); }, [loadState, loadHome]);

  // ---- auth ----
  const finishAuth = (data) => { setToken(data.token); setUser(data.user); return loadState(); };
  const signIn = async (email, password) => { const r = await api.post('/auth/login', { email, password }); await finishAuth(r.data); return r.data.user; };
  const register = async (name, email, password) => { const r = await api.post('/auth/register', { name, email, password }); await finishAuth(r.data); return r.data.user; };
  const social = async (provider) => { const r = await api.post('/auth/social', { provider, email: `${provider}.demo@clax.tv`, name: `${provider === 'google' ? 'Google' : 'Apple'} User` }); await finishAuth(r.data); return r.data.user; };
  const forgot = async (email) => (await api.post('/auth/forgot', { email })).data;
  const signOut = () => { setToken(null); setUser(null); setState(EMPTY); };

  const requireAuth = useCallback((action = 'do that') => {
    if (user) return true;
    toast.error(`Sign in to ${action}`, { action: { label: 'Sign in', onClick: () => { window.location.href = '/signin'; } } });
    return false;
  }, [user]);

  const patch = (fn) => setState((s) => ({ ...s, ...fn(s) }));
  const fail = (e) => toast.error(errMsg(e));

  // ---- list / follow ----
  const inMyList = useCallback((id) => state.myList.includes(id), [state.myList]);
  const toggleMyList = useCallback(async (id) => {
    if (!requireAuth('save to My List')) return;
    const has = state.myList.includes(id);
    patch((s) => ({ myList: has ? s.myList.filter((x) => x !== id) : [id, ...s.myList] }));
    try { has ? await api.delete(`/me/list/${id}`) : await api.post(`/me/list/${id}`); } catch (e) { fail(e); loadState(); }
  }, [state.myList, requireAuth, loadState]);
  const toggleFollow = useCallback(async (id) => {
    if (!requireAuth('follow podcasts')) return;
    const has = state.following.includes(id);
    patch((s) => ({ following: has ? s.following.filter((x) => x !== id) : [id, ...s.following] }));
    try { has ? await api.delete(`/me/follow/${id}`) : await api.post(`/me/follow/${id}`); } catch (e) { fail(e); loadState(); }
  }, [state.following, requireAuth, loadState]);

  // ---- progress / history ----
  const updateProgress = useCallback(async (contentId, data) => {
    if (!user) return;
    try {
      const r = await api.put(`/me/progress/${contentId}`, data);
      patch((s) => ({ progress: { ...s.progress, [contentId]: r.data }, history: [{ contentId, episodeId: data.episodeId, watchedAt: Date.now() }, ...s.history.filter((h) => !(h.contentId === contentId && h.episodeId === data.episodeId))].slice(0, 50) }));
    } catch { /* silent */ }
  }, [user]);
  const removeProgress = useCallback(async (id) => { patch((s) => { const p = { ...s.progress }; delete p[id]; return { progress: p }; }); try { await api.delete(`/me/progress/${id}`); } catch (e) { fail(e); } }, []);
  const clearHistory = useCallback(async () => { patch(() => ({ history: [] })); try { await api.delete('/me/history'); } catch (e) { fail(e); } }, []);
  const addHistory = useCallback(async (contentId, episodeId) => { if (!user) return; patch((s) => ({ history: [{ contentId, episodeId, watchedAt: Date.now() }, ...s.history.filter((h) => h.episodeId !== episodeId)].slice(0, 50) })); try { await api.post('/me/history', { contentId, episodeId }); } catch { /* silent */ } }, [user]);

  // ---- notifications ----
  const unreadCount = state.notifications.filter((n) => n.unread).length;
  const markAllRead = useCallback(async () => { if (!user) return; patch((s) => ({ notifications: s.notifications.map((n) => ({ ...n, unread: false })) })); try { await api.post('/me/notifications/read'); } catch { /* silent */ } }, [user]);

  // ---- settings ----
  const updateSettings = useCallback(async (upd) => {
    if (!user) return null;
    try { const r = await api.put('/me/settings', upd); setUser(r.data); patch(() => ({ ...(upd.country ? { country: upd.country } : {}), ...(upd.interests ? { interests: upd.interests } : {}), ...(upd.activeProfileId ? { activeProfileId: upd.activeProfileId } : {}) })); return r.data; } catch (e) { fail(e); return null; }
  }, [user]);
  const country = user ? state.country : guestCountry;
  const setCountry = useCallback((c) => { if (user) updateSettings({ country: c }); else { setGuestCountry(c); localStorage.setItem('clax:country', c); } }, [user, updateSettings]);
  const setInterests = useCallback((list) => updateSettings({ interests: list }), [updateSettings]);
  const setActiveProfileId = useCallback((id) => { patch(() => ({ activeProfileId: id })); updateSettings({ activeProfileId: id }); }, [updateSettings]);
  const changePlan = useCallback(async (planId) => { try { const r = await api.put('/me/subscription', { planId }); await loadState(); return r.data; } catch (e) { fail(e); return null; } }, [loadState]);

  // ---- profiles ----
  const addProfile = useCallback(async (p) => { try { const r = await api.post('/me/profiles', p); patch((s) => ({ profiles: [...s.profiles, r.data] })); return r.data; } catch (e) { fail(e); return null; } }, []);
  const updateProfile = useCallback(async (p) => { try { const r = await api.put(`/me/profiles/${p.id}`, p); patch((s) => ({ profiles: s.profiles.map((x) => (x.id === p.id ? r.data : x)) })); return r.data; } catch (e) { fail(e); return null; } }, []);
  const deleteProfile = useCallback(async (id) => { try { await api.delete(`/me/profiles/${id}`); await loadState(); return true; } catch (e) { fail(e); return false; } }, [loadState]);

  // ---- home config (admin) ----
  const setHomeCfg = useCallback(async (cfg) => {
    setHomeCfgState((h) => ({ ...h, ...cfg }));
    try { await api.put('/admin/home', { heroSlides: cfg.heroSlides, rows: cfg.rows }); } catch (e) { fail(e); loadHome(); }
  }, [loadHome]);

  // ---- rights ----
  const isAvailable = useCallback((content) => !content?.rights || content.rights.territories.includes(country), [country]);

  // ---- audio player ----
  useEffect(() => {
    const el = new Audio();
    el.preload = 'metadata';
    el.volume = 0.8;
    audioRef.current = el;
    const onTime = () => setAudio((a) => ({ ...a, currentTime: el.currentTime, duration: el.duration || a.duration }));
    const onEnd = () => setAudio((a) => ({ ...a, playing: false }));
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onTime);
    el.addEventListener('ended', onEnd);
    return () => { el.pause(); el.removeEventListener('timeupdate', onTime); el.removeEventListener('loadedmetadata', onTime); el.removeEventListener('ended', onEnd); };
  }, []);

  const playEpisode = useCallback((podcastId, episode) => {
    const el = audioRef.current;
    if (!el) return;
    setAudio((a) => {
      if (a.episode?.id === episode.id) {
        if (a.playing) { el.pause(); return { ...a, playing: false }; }
        el.play().catch(() => {});
        return { ...a, playing: true };
      }
      el.src = episode.audioUrl;
      el.currentTime = 0;
      el.play().catch(() => {});
      return { ...a, podcastId, episode, playing: true, currentTime: 0, duration: episode.duration * 60 };
    });
    addHistory(podcastId, episode.id);
  }, [addHistory]);
  const toggleAudio = useCallback(() => { const el = audioRef.current; if (!el || !el.src) return; setAudio((a) => { if (a.playing) el.pause(); else el.play().catch(() => {}); return { ...a, playing: !a.playing }; }); }, []);
  const seekAudio = useCallback((t) => { if (audioRef.current) { audioRef.current.currentTime = t; setAudio((a) => ({ ...a, currentTime: t })); } }, []);
  const setAudioVolume = useCallback((v) => { if (audioRef.current) audioRef.current.volume = v; setAudio((a) => ({ ...a, volume: v })); }, []);
  const closeAudio = useCallback(() => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.removeAttribute('src'); audioRef.current.load(); } setAudio((a) => ({ ...a, podcastId: null, episode: null, playing: false, currentTime: 0 })); }, []);

  const activeProfile = state.profiles.find((p) => p.id === state.activeProfileId) || state.profiles[0] || null;

  const value = useMemo(() => ({
    user, authLoading, signIn, register, social, forgot, signOut, requireAuth, updateSettings, changePlan, subscription: state.subscription,
    isAdmin: !!user?.roles?.includes('admin'), isCreator: !!(user?.roles?.includes('creator') || user?.roles?.includes('admin')),
    profiles: state.profiles, activeProfile, activeProfileId: state.activeProfileId, setActiveProfileId, addProfile, updateProfile, deleteProfile,
    myList: state.myList, toggleMyList, inMyList, following: state.following, toggleFollow,
    progress: state.progress, updateProgress, removeProgress, history: state.history, clearHistory,
    notifs: state.notifications, markAllRead, unreadCount, interests: state.interests, setInterests,
    homeCfg, setHomeCfg, reloadHome: loadHome, country, setCountry, isAvailable, reloadState: loadState,
    detailId, openDetail: setDetailId, closeDetail: () => setDetailId(null), detailContent: detailId ? byId(detailId) : null,
    audio, playEpisode, toggleAudio, seekAudio, setAudioVolume, closeAudio,
  }), [user, authLoading, state, activeProfile, homeCfg, country, detailId, audio, byId, requireAuth, updateSettings, changePlan, setActiveProfileId, addProfile, updateProfile, deleteProfile, toggleMyList, inMyList, toggleFollow, updateProgress, removeProgress, clearHistory, markAllRead, setInterests, setHomeCfg, loadHome, setCountry, isAvailable, loadState, playEpisode, toggleAudio, seekAudio, setAudioVolume, closeAudio]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);

export const fmtTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
export const fmtDuration = (min) => (min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`);
export const fmtNum = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : `${n ?? 0}`);
